import Amount from "@funds/components/atoms/amount";
import type { TransactionOrder } from "creso-core/types";
import { cn, Button, V2Loader } from "web-design-system";
import { Trash2 } from "lucide-react";
import { useContext, useMemo } from "react";
import { useDeleteTransactionOrder, useMandates } from "creso-core/hooks";
import { getOrderName } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import Units from "@funds/components/atoms/units";
import {
  OrderActionType,
  OrderAdditionalInfo,
  OrderFrequency,
  OrderUpdates,
} from "web-core";
import { TransactionContext } from "./transaction-wizard";

interface Props {
  order: TransactionOrder;
}

function getOrderInfo(order: TransactionOrder) {
  return (
    <div className="flex flex-row items-center gap-1">
      <OrderActionType order={order} />
      {!["CANCEL", "PAUSE", "RESUME"].includes(order.action_type) ? (
        <OrderFrequency order={order} />
      ) : null}
    </div>
  );
}

export default function OrderItemComponent(props: Props) {
  const {
    selectedOrder,
    updateContext,
    investment_account_guid = "",
  } = useContext(TransactionContext);

  const { amount, id, units } = props.order;

  const { mutateAsync: deleteOrder, isPending } = useDeleteTransactionOrder(
    investment_account_guid
  );

  const { mandates } = useMandates(investment_account_guid);

  const orderValue = useMemo(() => {
    if (amount) {
      return <Amount>{amount}</Amount>;
    }

    if (units) {
      return <Units>{units}</Units>;
    }

    return null;
  }, [amount, units]);

  return (
    <div
      title={getOrderName(props.order)}
      className={cn(
        "flex flex-col cursor-default hover:bg-secondary/70 transition-colors duration-100 rounded-lg p-3 group gap-3 items-start",
        props.order.id === selectedOrder?.id && "bg-secondary"
      )}
      onClick={() => {
        updateContext?.({
          selectedOrder:
            props.order.id === selectedOrder?.id ? null : props.order,
        });
      }}
    >
      <div className="flex flex-row justify-between w-full items-center">
        {getOrderInfo(props.order)}
        <span className="text-xs font-medium">{orderValue}</span>
      </div>
      <div className="flex flex-row items-center w-full gap-3">
        <V2Loader
          size="small"
          className="h-7 w-7 flex-shrink-0 flex-0"
          isLoading={isPending}
        >
          <AmcLogo amc_code={props.order.amc_morningstar_code} />
        </V2Loader>
        <div className="text-sm line-clamp-2">{getOrderName(props.order)}</div>
        <Button
          variant="ghost"
          className={cn(
            "invisible flex-shrink-0 ml-auto",
            "group-hover:visible"
          )}
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            if (props.order === selectedOrder) {
              updateContext?.({
                selectedOrder: null,
              });
            }
            deleteOrder(id);
          }}
        >
          <Trash2 />
        </Button>
      </div>
      <OrderAdditionalInfo order={props.order} />
      <OrderUpdates order={props.order} mandates={mandates} />
    </div>
  );
}
